/**
 * 订单表单弹窗「费用付款」区块：订单金额、已付金额、付款状态、付款方式与凭证上传（含订单抵消只读分支）
 */
import type { Dispatch, SetStateAction } from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import type { OrderFormData } from '../../OrderFormSchema';
import type { OrderItem } from '../../../../../types';
import { FileUploader } from '../../../../../components/feature/upload';
import { useProtectedUploadUrl } from '../../../../../hooks/useProtectedUploadUrl';
import { PAYMENT_METHODS } from '../../../constants';
import type { PaymentStatusStyle } from '../types';
import { SectionHeader } from '../SectionHeader';
import { inp, lbl } from '../utils';

interface AmountSectionProps {
  control: Control<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  form: OrderFormData;
  initial?: OrderItem | null;
  isNettedOrder: boolean;
  hasValidItems: boolean;
  itemsTotal: number;
  numDays: number | null;
  effectiveOrderAmount: string;
  overPayment: number;
  remainBalance: number;
  statusStyle: PaymentStatusStyle | null;
  isPaid: boolean;
  voucherLabel: string;
  paymentVoucherUrl: string;
  setPaymentVoucherUrl: Dispatch<SetStateAction<string>>;
  setSubmitError: Dispatch<SetStateAction<string | null>>;
}

export function AmountSection({ control, errors, form, initial, isNettedOrder, hasValidItems, itemsTotal, numDays, effectiveOrderAmount, overPayment, remainBalance, statusStyle, isPaid, voucherLabel, paymentVoucherUrl, setPaymentVoucherUrl, setSubmitError }: AmountSectionProps) {
  const voucherSrc = useProtectedUploadUrl(paymentVoucherUrl);
  const money = (v: number) => `¥${v.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}`;
  // 金额概览：待收余款逐单夹零，多收只在真的多收时才多占一格（极少数情况，不常驻）
  const summaryCells = [
    { label: '订单总额', value: money(Number(effectiveOrderAmount || 0)), color: 'text-gray-800' },
    { label: '已收款', value: money(Number(form.paidAmount || 0)), color: 'text-teal-600' },
    { label: '待收余款', value: money(remainBalance), color: remainBalance > 0 ? 'text-amber-600' : 'text-teal-600' },
    ...(overPayment > 0 ? [{ label: '多收', value: money(overPayment), color: 'text-sky-600' }] : []),
  ];
  return (
    /* ⑥ 费用付款 */
    <div>
      <SectionHeader icon="ri-money-cny-circle-line" title="费用付款" color="bg-emerald-50 text-emerald-500" />
      {isNettedOrder ? (
        /* 订单抵消后：付款信息只读显示 */
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>订单金额（元）</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">¥</span>
                <div className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600 font-semibold select-none flex items-center justify-between">
                  <span>{Number(effectiveOrderAmount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  <i className="ri-lock-line text-gray-400 text-xs" />
                </div>
              </div>
            </div>
            <div>
              <label className={lbl}>已付金额（元）</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">¥</span>
                <div className="w-full pl-7 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-600 font-semibold select-none flex items-center justify-between">
                  <span>{Number(form.paidAmount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  <i className="ri-lock-line text-gray-400 text-xs" />
                </div>
              </div>
            </div>
          </div>

          {/* 付款状态（0 元占位单整块不显示） */}
          {statusStyle && (
            <div>
              <label className={lbl}>付款状态</label>
              <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border ${statusStyle.bg}`}>
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className={`${statusStyle.icon} ${statusStyle.text} text-sm`} />
                </div>
                <span className={`text-sm font-semibold ${statusStyle.text}`}>{statusStyle.label}</span>
                <span className="ml-auto text-xs text-gray-400">订单抵消结算</span>
              </div>
            </div>
          )}

          {/* 抵消信息提示 */}
          <div>
            <label className={lbl}>付款方式</label>
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-teal-200 bg-teal-50">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-exchange-dollar-line text-teal-500 text-sm" />
              </div>
              <span className="text-sm font-medium text-teal-700">{initial?.paymentMethod}</span>
              <span className="text-xs text-teal-400 font-normal">· 对冲结算</span>
              <i className="ri-lock-line text-teal-400 text-xs ml-auto" />
            </div>
          </div>

          {/* 结清凭证只读提示 */}
          <div>
            <label className={lbl}>结清凭证</label>
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-teal-500 text-sm" />
              </div>
              <span className="text-xs text-gray-500">订单已通过抵消结算，付款记录不可修改</span>
              <i className="ri-lock-line text-gray-400 text-xs ml-auto" />
            </div>
          </div>
        </div>
      ) : (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>
              订单金额（元）
            </label>
            {hasValidItems ? (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">¥</span>
                <div className="w-full pl-7 pr-3 py-2 text-sm border border-sky-200 rounded-lg bg-sky-50/60 text-sky-700 font-semibold select-none flex items-center justify-between">
                  <span>{itemsTotal.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-lock-line text-sky-400 text-xs" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">¥</span>
                <Controller
                  name="orderAmount"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      {...field}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className={`${inp(errors.orderAmount?.message)} pl-7`}
                    />
                  )}
                />
              </div>
            )}
            {hasValidItems && (
              <div className="text-xs text-sky-500 mt-1 inline-flex items-center gap-1">
                <i className="ri-links-line" />
                由明细自动计算{numDays !== null && numDays > 1 ? `（× ${numDays}天）` : ''}
              </div>
            )}
            {errors.orderAmount && <p className="text-xs text-rose-500 mt-1">{errors.orderAmount.message}</p>}
          </div>
          <div>
            <label className={lbl}>已付金额（元）</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">¥</span>
              <Controller
                name="paidAmount"
                control={control}
                render={({ field }) => (
                  <input
                    type="number"
                    {...field}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className={`${inp(errors.paidAmount?.message)} pl-7`}
                  />
                )}
              />
            </div>
            {errors.paidAmount && <p className="text-xs text-rose-500 mt-1">{errors.paidAmount.message}</p>}
            {overPayment > 0 && (
              <p className="text-xs text-sky-500 mt-1 flex items-center gap-1">
                <i className="ri-information-line" />
                实收已超出订单金额，超出部分计入多收（预收/挂账）
              </p>
            )}
          </div>
        </div>

        {(effectiveOrderAmount || form.paidAmount) && (
          <div className={`grid gap-2 ${overPayment > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
            {summaryCells.map(item => (
              <div key={item.label} className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* 付款状态 — 自动计算只读显示；0 元占位单整块不显示 */}
        {statusStyle && (
          <div>
            <label className={lbl}>付款状态</label>
            <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border ${statusStyle.bg}`}>
              <div className="w-4 h-4 flex items-center justify-center shrink-0">
                <i className={`${statusStyle.icon} ${statusStyle.text} text-sm`} />
              </div>
              <span className={`text-sm font-semibold ${statusStyle.text}`}>{statusStyle.label}</span>
              <span className="ml-auto text-xs text-gray-400">根据金额自动判断</span>
            </div>
          </div>
        )}

        {/* 付款方式与付款日期 — 一分钱没收时整块不出（裁定 2026-10-02，与两端移动表单同一条闸门）：
            未付款的单摆一个空「请选择」下拉，只会让人以为漏填了什么东西 */}
        {isPaid && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl}>付款方式</label>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <select {...field} className={inp(errors.paymentMethod?.message)}>
                    <option value="">请选择</option>
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                )}
              />
              {errors.paymentMethod && <p className="text-xs text-rose-500 mt-1">{errors.paymentMethod.message}</p>}
            </div>
            <div>
              <label className={lbl}>付款日期</label>
              <Controller
                name="paymentDate"
                control={control}
                render={({ field }) => (
                  <input type="date" {...field} className={inp()} />
                )}
              />
            </div>
          </div>
        )}

        {/* 付款凭证上传 — 有付款记录时显示 */}
        {isPaid && (
          <div>
            <label className={lbl}>{voucherLabel}</label>
            <div className="space-y-3">
              {paymentVoucherUrl ? (
                <div className="relative">
                  <img src={voucherSrc || undefined} alt="付款凭证" className="max-h-32 object-contain mx-auto rounded border border-gray-100" />
                  <button
                    onClick={() => setPaymentVoucherUrl('')}
                    className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center text-rose-500 hover:bg-rose-50 transition-colors"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              ) : (
                <FileUploader
                  uploadUrl="/uploads/payment-voucher"
                  onUploadSuccess={(url) => setPaymentVoucherUrl(url)}
                  onUploadError={(error) => setSubmitError(error)}
                  acceptedTypes={['image/jpeg', 'image/png', 'image/gif']}
                  maxSize={5 * 1024 * 1024}
                  label="点击上传付款凭证"
                />
              )}
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}